import type { BaseFolder } from './folders';

export const EventType = {
  EQUIPMENT_CREATED: 'EQUIPMENT_CREATED',
  EQUIPMENT_UPDATED: 'EQUIPMENT_UPDATED',
  EQUIPMENT_DELETED: 'EQUIPMENT_DELETED',
  EQUIPMENT_INSPECTION_CREATED: 'EQUIPMENT_INSPECTION_CREATED',
  EQUIPMENT_STATUS_CHANGED: 'EQUIPMENT_STATUS_CHANGED',
  EQUIPMENT_LOST: 'EQUIPMENT_LOST',
  EQUIPMENT_ALLOCATED_TO_USER: 'EQUIPMENT_ALLOCATED_TO_USER',
  EQUIPMENT_UNALLOCATED_FROM_USER: 'EQUIPMENT_UNALLOCATED_FROM_USER',
  USER_CREATED: 'USER_CREATED',
  USER_UPDATED: 'USER_UPDATED',
  USER_DELETED: 'USER_DELETED',
  TAG_CREATED: 'TAG_CREATED',
  TAG_UPDATED: 'TAG_UPDATED',
  TAG_DELETED: 'TAG_DELETED',
  FOLDER_CREATED: 'FOLDER_CREATED',
  FOLDER_UPDATED: 'FOLDER_UPDATED',
  FOLDER_DELETED: 'FOLDER_DELETED',
  IMPORT_REQUESTED: 'IMPORT_REQUESTED',
  TRANSFER_CREATED: 'TRANSFER_CREATED',
  REPAIR_CREATED: 'REPAIR_CREATED',
  REPAIR_UPDATED: 'REPAIR_UPDATED',
  REPAIR_STARTED: 'REPAIR_STARTED',
  REPAIR_COMPLETED: 'REPAIR_COMPLETED',
  REPAIR_CANCELLED: 'REPAIR_CANCELLED',
  REPAIR_IRREPARABLE: 'REPAIR_IRREPARABLE',
  REPAIR_DELETED: 'REPAIR_DELETED',
  MEDICAL_VISIT_TYPE_CREATED: 'MEDICAL_VISIT_TYPE_CREATED',
  MEDICAL_VISIT_TYPE_UPDATED: 'MEDICAL_VISIT_TYPE_UPDATED',
  MEDICAL_VISIT_TYPE_DELETED: 'MEDICAL_VISIT_TYPE_DELETED',
  MEDICAL_VISIT_TYPES_ASSIGNED_TO_USER: 'MEDICAL_VISIT_TYPES_ASSIGNED_TO_USER',
  MEDICAL_VISIT_CREATED: 'MEDICAL_VISIT_CREATED',
  MEDICAL_VISIT_UPDATED: 'MEDICAL_VISIT_UPDATED',
  MEDICAL_VISIT_DELETED: 'MEDICAL_VISIT_DELETED',
  HABILITATION_TYPE_CREATED: 'HABILITATION_TYPE_CREATED',
  HABILITATION_TYPE_UPDATED: 'HABILITATION_TYPE_UPDATED',
  HABILITATION_TYPE_DELETED: 'HABILITATION_TYPE_DELETED',
  HABILITATION_CREATED: 'HABILITATION_CREATED',
  HABILITATION_UPDATED: 'HABILITATION_UPDATED',
  HABILITATION_DELETED: 'HABILITATION_DELETED',
  DISTRIBUTOR_ORGANIZATION_CREATED: 'DISTRIBUTOR_ORGANIZATION_CREATED',
  DISTRIBUTOR_ORGANIZATION_UPDATED: 'DISTRIBUTOR_ORGANIZATION_UPDATED',
  DISTRIBUTOR_USER_UPDATED: 'DISTRIBUTOR_USER_UPDATED',
  DISTRIBUTOR_IMPERSONATION_STARTED: 'DISTRIBUTOR_IMPERSONATION_STARTED',
  INCIDENT_DECLARED: 'INCIDENT_DECLARED',
  INCIDENT_UPDATED: 'INCIDENT_UPDATED',
  INCIDENT_CLOSED: 'INCIDENT_CLOSED',
  INCIDENT_NOT_RETAINED: 'INCIDENT_NOT_RETAINED',
  INCIDENT_REOPENED: 'INCIDENT_REOPENED',
  INCIDENT_ACTION_CREATED: 'INCIDENT_ACTION_CREATED',
  INCIDENT_ACTION_UPDATED: 'INCIDENT_ACTION_UPDATED',
  INCIDENT_ACTION_DELETED: 'INCIDENT_ACTION_DELETED',
  INCIDENT_QR_CODE_CREATED: 'INCIDENT_QR_CODE_CREATED',
  INCIDENT_QR_CODE_REVOKED: 'INCIDENT_QR_CODE_REVOKED',
} as const;

export type EventType = (typeof EventType)[keyof typeof EventType];

/**
 * Events about one employee's occupational health, and about the catalogue of
 * visit types the organization tracks.
 *
 * Both are hidden from anyone without `medicalVisit.list` /
 * `medicalVisitType.list`, whatever `activity.all` says: the activity feed must
 * never show more than the screen it describes. READER carries `activity.all`
 * and neither medical right — see EventsService.buildScopedEventWhere.
 */
export const medicalVisitEventTypes = [
  EventType.MEDICAL_VISIT_CREATED,
  EventType.MEDICAL_VISIT_UPDATED,
  EventType.MEDICAL_VISIT_DELETED,
  EventType.MEDICAL_VISIT_TYPES_ASSIGNED_TO_USER,
] as const;

export const medicalVisitTypeEventTypes = [
  EventType.MEDICAL_VISIT_TYPE_CREATED,
  EventType.MEDICAL_VISIT_TYPE_UPDATED,
  EventType.MEDICAL_VISIT_TYPE_DELETED,
] as const;

export const habilitationEventTypes = [
  EventType.HABILITATION_CREATED,
  EventType.HABILITATION_UPDATED,
  EventType.HABILITATION_DELETED,
] as const;

/**
 * Everything that happens to an incident. Hidden from anyone without
 * `incident.list`, whatever `activity.all` says — same rule as the medical
 * events above: a feed entry must never be easier to reach than the record it
 * describes, and an incident carries health-adjacent data.
 *
 * There is no self-only bucket here. An incident is not "about" one employee
 * the way a medical visit is: victims and witnesses are attached afterwards,
 * a declaration can be anonymous, and the declarant is often not the person
 * concerned. Falling back on "the employee themself" would therefore leak the
 * qualification of an incident to anyone merely listed as a witness.
 */
export const incidentEventTypes = [
  EventType.INCIDENT_DECLARED,
  EventType.INCIDENT_UPDATED,
  EventType.INCIDENT_CLOSED,
  EventType.INCIDENT_NOT_RETAINED,
  EventType.INCIDENT_REOPENED,
  EventType.INCIDENT_ACTION_CREATED,
  EventType.INCIDENT_ACTION_UPDATED,
  EventType.INCIDENT_ACTION_DELETED,
  EventType.INCIDENT_QR_CODE_CREATED,
  EventType.INCIDENT_QR_CODE_REVOKED,
] as const;

export const habilitationTypeEventTypes = [
  EventType.HABILITATION_TYPE_CREATED,
  EventType.HABILITATION_TYPE_UPDATED,
  EventType.HABILITATION_TYPE_DELETED,
] as const;

type EventLogUser = {
  id: number;
  firstName: string;
  lastName: string;
  externalUserId: string | null;
};

export type EventLog = {
  id: number;
  createdAt: Date;
  type: EventType;
  ownerUser: EventLogUser;
  equipment?: { id: number; name: string } | null;
  user?: EventLogUser | null;
  folder?: Pick<BaseFolder, 'id' | 'name'> | null;
  tag?: { id: number; name: string } | null;
  repair?: { id: number } | null;
  equipmentInspection?: { id: number } | null;
  // The visit type is named, never the outcome: no opinion, no restriction, no
  // date of examination. A feed entry says an action happened, not what it
  // found (see medicalVisitEventTypes).
  medicalVisit?: { id: number; type: { label: string } } | null;
  medicalVisitType?: { id: number; label: string } | null;
  habilitation?: { id: number; type: { label: string } } | null;
  habilitationType?: { id: number; label: string } | null;
  incident?: { id: number; reference: string } | null;
  deletedItemName?: string | null;
  targetOrganization?: { id: number; publicId: string; name: string } | null;
};
